import React, { useRef, useEffect, useCallback, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronRight } from 'lucide-react';

interface TutorialStep {
  id: string;
  title: string;
  description: string;
  target: string;
  position: string;
}

interface TutorialTooltipProps {
  step: TutorialStep;
  children?: React.ReactNode;
  showTutorial: boolean;
  currentTutorialStep: number;
  tutorialSteps: TutorialStep[];
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
}

export const TutorialTooltip: React.FC<TutorialTooltipProps> = React.memo(({
  step,
  children,
  showTutorial,
  currentTutorialStep,
  tutorialSteps,
  onNext,
  onPrev,
  onSkip
}) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ 
    top: number; 
    left: number; 
    position: string; 
    width?: number 
  }>({ top: 0, left: 0, position: 'bottom' });

  // Tutorial positioning for sidebar elements
  const updateTooltipPosition = useCallback(() => {
    const currentStep = tutorialSteps[currentTutorialStep];
    if (!currentStep) return;

    // Check if we're on mobile - if so, don't try to position tutorial
    const isMobile = window.innerWidth < 1024;
    if (isMobile) {
      console.log('Skipping tutorial positioning on mobile');
      return;
    }

    // Try multiple times to find the element
    let attempts = 0;
    const maxAttempts = 10;
    
    const tryPosition = () => {
      try {
        const targetElement = document.querySelector(currentStep.target);
        console.log('Looking for:', currentStep.target, 'Found:', targetElement);
        
        if (!targetElement && attempts < maxAttempts) {
          attempts++;
          setTimeout(tryPosition, 100);
          return;
        }
        
        if (!targetElement) {
          console.log('Target element not found after', maxAttempts, 'attempts:', currentStep.target);
          // Fallback position - center the tooltip
          setTooltipPosition({ 
            top: window.innerHeight / 2 - 110, 
            left: window.innerWidth / 2 - 160, 
            position: 'center',
            width: 320
          });
          return;
        }
      } catch (error) {
        console.error('Error in tutorial positioning:', error);
        // Safe fallback
        setTooltipPosition({ 
          top: 100, 
          left: 100, 
          position: 'center',
          width: 320
        });
        return;
      }

      try {
        const rect = targetElement.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        
        const tooltipWidth = 320;
        const tooltipHeight = 220; // More accurate height including padding
        const padding = 16;

        // Position to the right of sidebar elements (since they're on the left)
        // Center the tooltip more precisely with the target element
        let top = rect.top + (rect.height / 2) - (tooltipHeight / 2);
        let left = rect.right + padding;
        let position = 'right';

        // Fine-tune vertical centering - adjust for better alignment
        top = top - 10; // Small adjustment for better visual centering

        // Adjust if tooltip would go off screen
        if (left + tooltipWidth > viewportWidth - padding) {
          // Position to the left if no space on right
          left = rect.left - tooltipWidth - padding;
          position = 'left';
        }
        
        // Keep tooltip vertically in viewport
        if (top < padding) {
          top = padding;
        } else if (top + tooltipHeight > viewportHeight - padding) {
          top = viewportHeight - tooltipHeight - padding;
        }

        // Ensure we don't go off the left edge
        if (left < padding) {
          left = padding;
          position = 'top';
          top = rect.bottom + padding;
        }

        console.log('Positioning tooltip:', { top, left, position, rect });

        setTooltipPosition({ 
          top, 
          left, 
          position,
          width: tooltipWidth
        });
      } catch (positionError) {
        console.error('Error calculating tooltip position:', positionError);
        // Fallback to center
        setTooltipPosition({ 
          top: window.innerHeight / 2 - 110, 
          left: window.innerWidth / 2 - 160, 
          position: 'center',
          width: 320
        });
      }
    };

    tryPosition();
  }, [currentTutorialStep, tutorialSteps]);

  useEffect(() => {
    if (showTutorial && tutorialSteps[currentTutorialStep]?.id === step.id) {
      // Initial positioning
      updateTooltipPosition();
      
      // Re-position after tooltip is rendered with actual dimensions
      const timeoutId = setTimeout(() => {
        if (tooltipRef.current) {
          const currentStep = tutorialSteps[currentTutorialStep];
          if (!currentStep) return;
          
          const targetElement = document.querySelector(currentStep.target);
          if (targetElement && tooltipRef.current) {
            const rect = targetElement.getBoundingClientRect();
            const tooltipRect = tooltipRef.current.getBoundingClientRect();
            
            // More precise centering using actual tooltip height
            const newTop = rect.top + (rect.height / 2) - (tooltipRect.height / 2);
            const newLeft = rect.right + 16;
            
            setTooltipPosition(prev => ({
              ...prev,
              top: Math.max(16, Math.min(newTop, window.innerHeight - tooltipRect.height - 16)),
              left: newLeft
            }));
          }
        }
      }, 100);
      
      window.addEventListener('resize', updateTooltipPosition);
      window.addEventListener('scroll', updateTooltipPosition);
      
      return () => {
        clearTimeout(timeoutId);
        window.removeEventListener('resize', updateTooltipPosition);
        window.removeEventListener('scroll', updateTooltipPosition);
      };
    }
  }, [showTutorial, currentTutorialStep, step.id, updateTooltipPosition]);

  const getArrowClasses = (position: string) => {
    const baseClasses = "absolute w-4 h-4 bg-white border transform rotate-45";
    
    switch (position) {
      case 'top':
        return `${baseClasses} -bottom-2 left-1/2 -translate-x-1/2 border-b border-r border-gray-200`;
      case 'bottom':
        return `${baseClasses} -top-2 left-1/2 -translate-x-1/2 border-t border-l border-gray-200`;
      case 'left':
        return `${baseClasses} -right-2 top-1/2 -translate-y-1/2 border-t border-r border-gray-200`;
      case 'right':
        return `${baseClasses} -left-2 top-1/2 -translate-y-1/2 border-b border-l border-gray-200`;
      default:
        return `${baseClasses} -top-2 left-1/2 -translate-x-1/2 border-t border-l border-gray-200`;
    }
  };

  // Don't render tutorial on mobile devices
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 1024;
  
  if (!showTutorial || tutorialSteps[currentTutorialStep]?.id !== step.id || isMobile) {
    return children ? <>{children}</> : null;
  }

  return (
    <>
      {children}
      <AnimatePresence>
        <motion.div
          ref={tooltipRef}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className="fixed z-[9999] bg-white rounded-xl shadow-2xl border border-gray-200 p-6"
          style={{
            top: tooltipPosition.top,
            left: tooltipPosition.left,
            width: tooltipPosition.width || (window.innerWidth < 768 ? 'calc(100vw - 32px)' : '320px'),
            maxWidth: window.innerWidth < 768 ? 'calc(100vw - 32px)' : '400px'
          }}
        >
          <div className={getArrowClasses(tooltipPosition.position)}></div>
          
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-sm font-bold">{currentTutorialStep + 1}</span>
              </div>
              <span className="text-sm text-gray-500">Step {currentTutorialStep + 1} of {tutorialSteps.length}</span>
            </div>
            <button
              onClick={onSkip}
              className="text-gray-400 hover:text-gray-600 p-1"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{step.title}</h3>
          <p className="text-gray-600 mb-4">{step.description}</p>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              {currentTutorialStep > 0 && (
                <button
                  onClick={onPrev}
                  className="text-gray-600 hover:text-gray-800 text-sm font-medium px-3 py-1 rounded border border-gray-300 hover:bg-gray-50 transition-colors"
                >
                  Back
                </button>
              )}
              <button
                onClick={onSkip}
                className="text-gray-600 hover:text-gray-800 text-sm font-medium"
              >
                Skip tutorial
              </button>
            </div>
            <button
              onClick={onNext}
              className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-2 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all flex items-center space-x-2"
            >
              <span>{currentTutorialStep === tutorialSteps.length - 1 ? 'Finish' : 'Next'}</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      </AnimatePresence>
    </>
  );
});

TutorialTooltip.displayName = 'TutorialTooltip'; 